import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BlockStack, Card, DataTable, InlineStack, Link, Select, SkeletonBodyText, Tabs, Text } from '@shopify/polaris';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatDate, formatToCurrency, getRequest } from '../../../shared/components/functions';
import { CardHeading } from '../../../shared/components/cards/CardHeading';
import { formatCompactCurrency, formatDays, formatInteger, toNumber } from '../../analytics/components/deep/analyticsHelpers';
import CompanyTotalSales from './CompanyTotalSales';
import CompanyInvoiceLines from './CompanyInvoiceLines';

const OUT_COLOR = '#64BE7B'; // money coming in from them (customer side)
const IN_COLOR = '#AD0002'; // money going out to them (supplier side)
const NET_COLOR = '#5C6AC4';

const AGING_BUCKETS = [
  { key: 'not_due', label: 'Not yet due' },
  { key: 'd1_30', label: '1–30 d overdue' },
  { key: 'd31_60', label: '31–60 d overdue' },
  { key: 'd61_90', label: '61–90 d overdue' },
  { key: 'd91_180', label: '91–180 d overdue' },
  { key: 'd180_plus', label: '180+ d overdue' },
  { key: 'no_due_date', label: 'No due date' }
];

function Stat({ label, value, sub, tone }) {
  return (
    <div style={{ minWidth: '140px' }}>
      <Text as="p" variant="bodySm" tone="subdued">
        {label}
      </Text>
      <Text as="p" variant="headingLg" tone={tone}>
        {value}
      </Text>
      {sub ? (
        <Text as="p" variant="bodySm" tone="subdued">
          {sub}
        </Text>
      ) : null}
    </div>
  );
}

function StatGrid({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginTop: '12px' }}>{children}</div>;
}

function shortDate(value) {
  return value ? formatDate(value) : '—';
}

function yearOf(value) {
  return value ? String(value).substring(0, 4) : '—';
}

function CustomerSummaryCard({ summary }) {
  if (!summary) return null;
  const c = summary.customer || {};
  const hasData = toNumber(c.invoice_count) > 0 || toNumber(c.sale_count) > 0 || toNumber(c.quotation_count) > 0;
  const netInvoiced = toNumber(c.invoiced_incl) - toNumber(c.credited_incl);
  return (
    <Card>
      <CardHeading title="As customer — what they buy from us" />
      {hasData ? (
        <StatGrid>
          <Stat
            label="Net invoiced (incl. VAT)"
            value={formatToCurrency(netInvoiced)}
            sub={`${formatInteger(c.invoice_count)} invoices · ${formatInteger(c.creditnote_count)} credit notes (${formatCompactCurrency(c.credited_incl)})`}
          />
          <Stat
            label="Open receivable"
            value={formatToCurrency(c.open_amount)}
            tone={toNumber(c.expired_open) > 0 ? 'critical' : undefined}
            sub={toNumber(c.expired_count) > 0 ? `${formatInteger(c.expired_count)} expired (${formatCompactCurrency(c.expired_open)})` : 'Nothing expired'}
          />
          <Stat label="Sale orders (excl. VAT)" value={formatToCurrency(c.sales_excl)} sub={`${formatInteger(c.sale_count)} orders · last ${shortDate(c.last_sale_date)}`} />
          <Stat label="Quotations" value={formatInteger(c.quotation_count)} sub={c.last_quotation_date ? `Last ${shortDate(c.last_quotation_date)}` : null} />
          <Stat label="They pay us in" value={c.avg_payment_days != null ? formatDays(c.avg_payment_days) : '—'} sub={`${formatInteger(c.paid_count)} paid invoices`} />
          <Stat label="Customer since" value={yearOf(c.first_invoice_date)} sub={`Last invoice ${shortDate(c.last_invoice_date)}`} />
          <Stat label="Machines on site" value={formatInteger(c.machine_count)} />
        </StatGrid>
      ) : (
        <Text as="p" tone="subdued">
          No customer relationship: no invoices, sale orders or quotations for this company.
        </Text>
      )}
    </Card>
  );
}

function SupplierSummaryCard({ summary }) {
  if (!summary) return null;
  const s = summary.supplier || {};
  const hasData = toNumber(s.invoice_count) > 0 || toNumber(s.purchase_count) > 0 || toNumber(s.product_count) > 0;
  const netInvoiced = toNumber(s.invoiced_incl) - toNumber(s.credited_incl);
  return (
    <Card>
      <CardHeading title="As supplier — what we buy from them" />
      {hasData ? (
        <StatGrid>
          <Stat
            label="Net invoiced in (incl. VAT)"
            value={formatToCurrency(netInvoiced)}
            sub={`${formatInteger(s.invoice_count)} invoices · ${formatInteger(s.creditnote_count)} credit notes (${formatCompactCurrency(s.credited_incl)})`}
          />
          <Stat
            label="Open payable"
            value={formatToCurrency(s.open_amount)}
            tone={toNumber(s.expired_open) > 0 ? 'critical' : undefined}
            sub={toNumber(s.expired_count) > 0 ? `${formatInteger(s.expired_count)} expired (${formatCompactCurrency(s.expired_open)})` : 'Nothing expired'}
          />
          <Stat
            label="Purchase orders"
            value={formatInteger(s.purchase_count)}
            sub={`${formatCompactCurrency(s.purchases_excl)} on priced lines (excl. VAT) · last ${shortDate(s.last_purchase_date)}`}
          />
          <Stat label="Inquiries" value={formatInteger(s.inquiry_count)} sub={s.last_inquiry_date ? `Last ${shortDate(s.last_inquiry_date)}` : null} />
          <Stat label="We pay them in" value={s.avg_payment_days != null ? formatDays(s.avg_payment_days) : '—'} sub={`${formatInteger(s.paid_count)} paid invoices`} />
          <Stat label="Supplier since" value={yearOf(s.first_invoice_date)} sub={`Last invoice ${shortDate(s.last_invoice_date)}`} />
          <Stat label="Products supplied" value={formatInteger(s.product_count)} sub={`${formatInteger(s.active_product_count)} active`} />
        </StatGrid>
      ) : (
        <Text as="p" tone="subdued">
          No supplier relationship: no incoming invoices, purchase orders or supplied products for this company.
        </Text>
      )}
    </Card>
  );
}

function FlowsTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <Card>
      <Text as="p" variant="headingSm">
        {label}
      </Text>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color, margin: 0 }}>
          {entry.name}: {formatToCurrency(entry.value)}
        </p>
      ))}
    </Card>
  );
}

function YearlyFlowsCard({ flows, loading }) {
  const chartData = useMemo(
    () =>
      (flows || []).map((row) => ({
        ...row,
        net_balance: toNumber(row.invoiced_out_incl) - toNumber(row.credited_out_incl) - (toNumber(row.invoiced_in_incl) - toNumber(row.credited_in_incl))
      })),
    [flows]
  );

  const totals = useMemo(() => {
    const keys = ['invoices_out_count', 'invoiced_out_incl', 'credited_out_incl', 'invoices_in_count', 'invoiced_in_incl', 'credited_in_incl', 'sales_excl', 'purchases_excl', 'net_balance'];
    return keys.reduce((acc, key) => ({ ...acc, [key]: chartData.reduce((sum, row) => sum + toNumber(row[key]), 0) }), {});
  }, [chartData]);

  const rows = useMemo(() => {
    const yearRows = [...chartData]
      .sort((a, b) => b.year - a.year)
      .map((row) => [
        <b>{row.year}</b>,
        formatInteger(row.invoices_out_count),
        formatToCurrency(row.invoiced_out_incl),
        toNumber(row.credited_out_incl) > 0 ? formatToCurrency(row.credited_out_incl) : '—',
        formatInteger(row.invoices_in_count),
        formatToCurrency(row.invoiced_in_incl),
        toNumber(row.credited_in_incl) > 0 ? formatToCurrency(row.credited_in_incl) : '—',
        toNumber(row.sales_excl) !== 0 ? formatToCurrency(row.sales_excl) : '—',
        toNumber(row.purchases_excl) !== 0 ? formatToCurrency(row.purchases_excl) : '—',
        <Text as="span" tone={row.net_balance >= 0 ? 'success' : 'critical'}>
          {formatToCurrency(row.net_balance)}
        </Text>
      ]);
    if (yearRows.length > 1) {
      yearRows.unshift([
        <b>All years</b>,
        <b>{formatInteger(totals.invoices_out_count)}</b>,
        <b>{formatToCurrency(totals.invoiced_out_incl)}</b>,
        <b>{formatToCurrency(totals.credited_out_incl)}</b>,
        <b>{formatInteger(totals.invoices_in_count)}</b>,
        <b>{formatToCurrency(totals.invoiced_in_incl)}</b>,
        <b>{formatToCurrency(totals.credited_in_incl)}</b>,
        <b>{formatToCurrency(totals.sales_excl)}</b>,
        <b>{formatToCurrency(totals.purchases_excl)}</b>,
        <Text as="span" tone={totals.net_balance >= 0 ? 'success' : 'critical'}>
          <b>{formatToCurrency(totals.net_balance)}</b>
        </Text>
      ]);
    }
    return yearRows;
  }, [chartData, totals]);

  return (
    <Card>
      <CardHeading title="Yearly money flows" />
      <Text tone="subdued" as="p">
        Invoiced to them (revenue) versus invoiced by them (costs) per year, incl. VAT. The net line is invoiced out minus invoiced in, both corrected for credit notes. Sale and purchase order columns
        are excl. VAT.
      </Text>
      {loading ? (
        <SkeletonBodyText lines={10} />
      ) : chartData.length === 0 ? (
        <Text as="p" tone="subdued">
          No invoices in either direction yet.
        </Text>
      ) : (
        <BlockStack gap="400">
          <div style={{ width: '100%', height: '320px', marginTop: '16px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 6" opacity={0.3} />
                <XAxis dataKey="year" tick={{ fontSize: 13 }} />
                <YAxis tick={{ fontSize: 13 }} tickFormatter={formatCompactCurrency} />
                <Tooltip content={<FlowsTooltip />} />
                <Legend />
                <Bar dataKey="invoiced_out_incl" name="Invoiced to them" fill={OUT_COLOR} radius={[3, 3, 0, 0]} />
                <Bar dataKey="invoiced_in_incl" name="Invoiced by them" fill={IN_COLOR} radius={[3, 3, 0, 0]} />
                <Line type="monotone" dataKey="net_balance" name="Net balance" stroke={NET_COLOR} strokeWidth={3} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <DataTable
            verticalAlign="middle"
            increasedTableDensity
            stickyHeader
            columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
            headings={[<b>Year</b>, <b># out</b>, <b>Invoiced out</b>, <b>CN out</b>, <b># in</b>, <b>Invoiced in</b>, <b>CN in</b>, <b>Sale orders</b>, <b>Purchase orders</b>, <b>Net balance</b>]}
            rows={rows}
          />
        </BlockStack>
      )}
    </Card>
  );
}

const TOP_PRODUCT_TABS = [
  { id: 'sold', content: 'Sold to them', subtitle: 'Ranked by invoiced value (invoice lines, excl. VAT).' },
  { id: 'bought_orders', content: 'Bought from them (purchase orders)', subtitle: 'Ranked by purchase order line value (excl. VAT), canceled orders excluded.' },
  { id: 'bought_invoices', content: 'Bought from them (invoices in)', subtitle: 'Ranked by incoming invoice line value. Only invoices booked with lines show up here.' }
];

function TopArticlesCard({ FIRNR, years }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [year, setYear] = useState('all');
  const [limit, setLimit] = useState('25');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const direction = TOP_PRODUCT_TABS[selectedTab].id;

  const fetchData = useCallback(async () => {
    if (!FIRNR) return;
    setLoading(true);
    const params = new URLSearchParams({ companyId: FIRNR, direction, limit });
    if (year !== 'all') params.append('year', year);
    const data = await getRequest(`/api/metrics/company-top-products?${params}`);
    setItems(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [FIRNR, direction, year, limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const yearOptions = useMemo(() => [{ label: 'All time', value: 'all' }, ...[...(years || [])].sort((a, b) => b - a).map((y) => ({ label: String(y), value: String(y) }))], [years]);

  const rows = items.map((item) => [
    item.product_id > 0 ? (
      <Link removeUnderline url={`/products/${item.product_id}`}>
        {item.product_name || `#${item.product_id}`}
      </Link>
    ) : (
      <Text as="span" tone="subdued">
        {item.product_name}
      </Text>
    ),
    item.product_ref || '—',
    formatInteger(item.line_count),
    Number(item.total_qty).toFixed(2),
    formatToCurrency(item.total_value),
    toNumber(item.total_qty) !== 0 ? formatToCurrency(toNumber(item.total_value) / toNumber(item.total_qty)) : '—',
    shortDate(item.first_date),
    shortDate(item.last_date)
  ]);

  return (
    <Card>
      <InlineStack align="space-between" blockAlign="start">
        <div>
          <CardHeading title="Top articles" />
          <Text tone="subdued" as="p">
            {TOP_PRODUCT_TABS[selectedTab].subtitle}
          </Text>
        </div>
        <InlineStack gap="200">
          <Select label="Year" labelHidden options={yearOptions} value={year} onChange={setYear} />
          <Select
            label="Limit"
            labelHidden
            options={[
              { label: 'Top 10', value: '10' },
              { label: 'Top 25', value: '25' },
              { label: 'Top 50', value: '50' }
            ]}
            value={limit}
            onChange={setLimit}
          />
        </InlineStack>
      </InlineStack>
      <Tabs tabs={TOP_PRODUCT_TABS} selected={selectedTab} onSelect={setSelectedTab} fitted>
        <div style={{ paddingTop: '8px' }}>
          {loading ? (
            <SkeletonBodyText lines={8} />
          ) : rows.length > 0 ? (
            <DataTable
              verticalAlign="middle"
              increasedTableDensity
              stickyHeader
              columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'text', 'text']}
              headings={[<b>Product</b>, <b>Ref</b>, <b>Lines</b>, <b>Qty</b>, <b>Total value</b>, <b>Avg unit price</b>, <b>First</b>, <b>Last</b>]}
              rows={rows}
            />
          ) : (
            <Text as="p" tone="subdued">
              No lines found for this selection.
            </Text>
          )}
        </div>
      </Tabs>
    </Card>
  );
}

function AgingTable({ title, aging }) {
  const byBucket = Object.fromEntries((aging || []).map((row) => [row.bucket, row]));
  const rows = AGING_BUCKETS.filter((bucket) => byBucket[bucket.key]).map((bucket) => [
    bucket.label,
    formatInteger(byBucket[bucket.key].invoice_count),
    formatToCurrency(byBucket[bucket.key].open_amount)
  ]);
  return (
    <div style={{ flex: 1, minWidth: '280px' }}>
      <Text as="h3" variant="headingSm">
        {title}
      </Text>
      {rows.length > 0 ? (
        <DataTable verticalAlign="middle" increasedTableDensity columnContentTypes={['text', 'numeric', 'numeric']} headings={[<b>Bucket</b>, <b>#</b>, <b>Open</b>]} rows={rows} />
      ) : (
        <Text as="p" tone="subdued">
          Nothing open.
        </Text>
      )}
    </div>
  );
}

function PaymentBehaviorCard({ behavior, summary, loading }) {
  const chartData = useMemo(() => {
    const byYear = {};
    (behavior?.receivables || []).forEach((row) => {
      byYear[row.year] = { year: row.year, they_pay_days: row.avg_days != null ? Number(Number(row.avg_days).toFixed(1)) : null };
    });
    (behavior?.payables || []).forEach((row) => {
      byYear[row.year] = { ...(byYear[row.year] || { year: row.year }), we_pay_days: row.avg_days != null ? Number(Number(row.avg_days).toFixed(1)) : null };
    });
    return Object.values(byYear).sort((a, b) => a.year - b.year);
  }, [behavior]);

  return (
    <Card>
      <CardHeading title="Payment behavior" />
      <Text tone="subdued" as="p">
        Average days from invoice date to payment, per year: how fast they pay our invoices versus how fast we pay theirs. Below: aging of everything that is open right now.
      </Text>
      {loading ? (
        <SkeletonBodyText lines={8} />
      ) : (
        <BlockStack gap="400">
          {chartData.length > 0 ? (
            <div style={{ width: '100%', height: '260px', marginTop: '16px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 6" opacity={0.3} />
                  <XAxis dataKey="year" tick={{ fontSize: 13 }} />
                  <YAxis tick={{ fontSize: 13 }} unit=" d" />
                  <Tooltip formatter={(value) => `${value} days`} />
                  <Legend />
                  <Line type="monotone" dataKey="they_pay_days" name="They pay us in (days)" stroke={OUT_COLOR} strokeWidth={3} dot={{ r: 4 }} connectNulls />
                  <Line type="monotone" dataKey="we_pay_days" name="We pay them in (days)" stroke={IN_COLOR} strokeWidth={3} dot={{ r: 4 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <Text as="p" tone="subdued">
              No paid invoices in either direction yet.
            </Text>
          )}
          {(summary?.receivablesAging?.length > 0 || summary?.payablesAging?.length > 0) && (
            <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
              <AgingTable title="Open receivables (they owe us)" aging={summary?.receivablesAging} />
              <AgingTable title="Open payables (we owe them)" aging={summary?.payablesAging} />
            </div>
          )}
        </BlockStack>
      )}
    </Card>
  );
}

export default function CompanyAnalytics({ FIRNR }) {
  const [summary, setSummary] = useState(null);
  const [flows, setFlows] = useState([]);
  const [behavior, setBehavior] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!FIRNR) return;
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      const [summaryData, flowsData, behaviorData] = await Promise.all([
        getRequest(`/api/metrics/company-relationship-summary?companyId=${FIRNR}`),
        getRequest(`/api/metrics/company-yearly-flows?companyId=${FIRNR}`),
        getRequest(`/api/metrics/company-payment-behavior?companyId=${FIRNR}`)
      ]);
      if (cancelled) return;
      setSummary(summaryData || null);
      setFlows(Array.isArray(flowsData) ? flowsData : []);
      setBehavior(behaviorData || null);
      setLoading(false);
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [FIRNR]);

  const years = useMemo(() => flows.map((row) => row.year), [flows]);

  return (
    <BlockStack gap="400">
      {loading ? (
        <Card>
          <SkeletonBodyText lines={6} />
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '16px' }}>
          <CustomerSummaryCard summary={summary} />
          <SupplierSummaryCard summary={summary} />
        </div>
      )}
      <YearlyFlowsCard flows={flows} loading={loading} />
      <TopArticlesCard FIRNR={FIRNR} years={years} />
      <PaymentBehaviorCard behavior={behavior} summary={summary} loading={loading} />
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'stretch' }}>
        <div style={{ flex: 1, minWidth: '400px' }}>
          <CompanyTotalSales FIRNR={FIRNR} />
        </div>
        <div style={{ flex: 1, minWidth: '400px' }}>
          <CompanyInvoiceLines FIRNR={FIRNR} />
        </div>
      </div>
    </BlockStack>
  );
}
